// groupSlots — a server-computed needs-review list, grouped into the places a
// person actually clears.
//
// ── THIS IS A PLAIN FUNCTION, NOT A HOOK ────────────────────────────────────
// Deliberately. It calls nothing from React and must never start to:
// PoImportDocumentCard builds its slots BELOW an early return (it bails when a
// document has no extraction), so anything hook-shaped in here would be a
// conditional hook in a component that reads fine and crashes on the rare
// document. Callers wrap it in their own useMemo when they want one, which also
// keeps the memo deps next to the state they watch.
//
// ── WHICH LANES USE IT, AND WHY NOT ALL FIVE ────────────────────────────────
// The three map lanes do: quote-to-draft-PO, catalog import, packing list. Their
// loops were byte-identical, differing only in the entry type flowing through.
//
// The other two keep their own builders on purpose, and neither is a candidate:
// the import lane's card needs a per-call factory that ALSO records which keys it
// anchored (a side effect this function has no business carrying), and the scan
// page needs an ARRAY rather than a map, which is the whole of its difference —
// it wears the same amber ring as everyone else. Forcing either through one
// signature costs more in options than it saves in lines.

import { reviewKey, slotLabel, type ReviewResolution } from './grounding';
import type { ReviewSlot } from './ReviewNote';

/**
 * One entry as this function reads it: the display shape ReviewNote renders, so
 * any lane's own review-entry type is assignable without weakening it at source.
 */
export type ReviewSlotEntry = ReviewSlot['entries'][number];

export interface GroupSlotsOptions {
  /** Appended to a flagged input's className while the slot is open. */
  ringClass: string;
  /** Has this key already been answered? Absent from the store means open. */
  isResolved: (key: string) => boolean;
  /** Record how the person answered it: typed in the field, or looked and left it. */
  resolve: (key: string, resolution: ReviewResolution) => void;
}

/**
 * Grouped by reviewKey so one amber note covers every complaint about the same
 * place on the page, and one "Looks right" clears all of them. Same grouping the
 * server uses, so the two agree about what is outstanding.
 */
export function groupSlots(
  entries: ReadonlyArray<ReviewSlotEntry>,
  { ringClass, isResolved, resolve }: GroupSlotsOptions
): Map<string, ReviewSlot> {
  const map = new Map<string, ReviewSlot>();
  for (const entry of entries) {
    const key = reviewKey(entry);
    if (!map.has(key)) {
      const open = !isResolved(key);
      map.set(key, {
        key,
        entries: [],
        open,
        label: slotLabel(entry.target, entry.field, entry.line_number),
        ring: open ? ringClass : '',
        markEdited: () => resolve(key, 'edited'),
        looksRight: () => resolve(key, 'confirmed_as_staged'),
      });
    }
    map.get(key)!.entries.push(entry);
  }
  return map;
}

export default groupSlots;
